import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  configureConnector,
  deleteLLMApiKey,
  disconnectConnector,
  fetchActionLogs,
  fetchConnectorSummaries,
  fetchIntegrationSettings,
  fetchLLMSettings,
  fetchPolicyDocument,
  fetchUsageSummary,
  fetchUsageTimeline,
  fetchDailyUsage,
  fetchApprovalRoleMappings,
  updateApprovalRoleMappings,
  updateIntegrationSettings,
  deleteIntegrationSettings,
  updateLLMApiKey,
  updatePolicyDocument,
  type ActionLogEntry,
  type ConnectorSummary,
  type IntegrationSettingsEntry,
  type LLMSettingsResponse,
  type PolicyDocumentResponse,
  type UsageSummaryResponse,
  type UsageTimelineResponse,
  type DailyUsageResponse,
  type WorkspaceRole,
} from '../services/settings';

export const settingsKeys = {
  root: ['settings'] as const,
  llm: () => [...settingsKeys.root, 'llm'] as const,
  policy: () => [...settingsKeys.root, 'policy'] as const,
  integrations: () => [...settingsKeys.root, 'integrations'] as const,
  connectors: () => [...settingsKeys.root, 'connectors'] as const,
  usage: () => [...settingsKeys.root, 'usage'] as const,
  usageTimeline: () => [...settingsKeys.root, 'usage_timeline'] as const,
  dailyUsage: (date: string) => [...settingsKeys.root, 'daily_usage', date] as const,
  logs: (limit: number) => [...settingsKeys.root, 'logs', limit] as const,
  approvalRoles: () => [...settingsKeys.root, 'approval_roles'] as const,
};

export const useLLMSettings = () => {
  return useQuery<LLMSettingsResponse, Error>(settingsKeys.llm(), fetchLLMSettings, {
    staleTime: 60_000,
  });
};

export const useUpdateLLMKey = () => {
  const client = useQueryClient();
  return useMutation(updateLLMApiKey, {
    onSuccess: () => {
      client.invalidateQueries({ queryKey: settingsKeys.llm() });
    },
  });
};

export const useDeleteLLMKey = () => {
  const client = useQueryClient();
  return useMutation(deleteLLMApiKey, {
    onSuccess: () => {
      client.invalidateQueries({ queryKey: settingsKeys.llm() });
    },
  });
};

export const usePolicyDocument = () => {
  return useQuery<PolicyDocumentResponse, Error>(settingsKeys.policy(), fetchPolicyDocument, {
    staleTime: 60_000,
  });
};

export const useUpdatePolicyDocument = () => {
  const client = useQueryClient();
  return useMutation(updatePolicyDocument, {
    onSuccess: () => {
      client.invalidateQueries({ queryKey: settingsKeys.policy() });
    },
  });
};

export const useConnectorSummaries = () => {
  return useQuery<ConnectorSummary[], Error>(settingsKeys.connectors(), fetchConnectorSummaries, {
    staleTime: 30_000,
  });
};

export const useIntegrationSettings = () => {
  return useQuery<IntegrationSettingsEntry[], Error>(
    settingsKeys.integrations(),
    fetchIntegrationSettings,
    {
      staleTime: 30_000,
    },
  );
};

export const useConfigureConnector = () => {
  const client = useQueryClient();
  return useMutation(configureConnector, {
    onSuccess: () => {
      client.invalidateQueries({ queryKey: settingsKeys.connectors() });
    },
  });
};

export const useUpdateIntegrationSettings = () => {
  const client = useQueryClient();
  return useMutation(updateIntegrationSettings, {
    onSuccess: () => {
      client.invalidateQueries({ queryKey: settingsKeys.integrations() });
    },
  });
};

export const useDeleteIntegrationSettings = () => {
  const client = useQueryClient();
  return useMutation(deleteIntegrationSettings, {
    onSuccess: () => {
      client.invalidateQueries({ queryKey: settingsKeys.integrations() });
    },
  });
};

export const useDisconnectConnector = () => {
  const client = useQueryClient();
  return useMutation(disconnectConnector, {
    onSuccess: () => {
      client.invalidateQueries({ queryKey: settingsKeys.connectors() });
    },
  });
};

export const useUsageSummary = () => {
  return useQuery<UsageSummaryResponse, Error>(settingsKeys.usage(), fetchUsageSummary, {
    refetchInterval: 60_000,
  });
};

export const useUsageTimeline = () => {
  return useQuery<UsageTimelineResponse, Error>(
    settingsKeys.usageTimeline(),
    fetchUsageTimeline,
    {
      staleTime: 5 * 60_000, // Consider data fresh for 5 minutes, but don't auto-refetch
    },
  );
};

export const useDailyUsage = (date: string | null) => {
  return useQuery<DailyUsageResponse, Error>(
    date ? settingsKeys.dailyUsage(date) : ['settings', 'daily_usage', 'disabled'],
    () => fetchDailyUsage(date as string),
    {
      enabled: Boolean(date),
      staleTime: 5 * 60_000, // Consider data fresh for 5 minutes, but don't auto-refetch
    },
  );
};

/**
 * Hook that returns a function to invalidate usage-related queries.
 * Call the returned function after a new message/response is received
 * to refresh the usage timeline and summary.
 */
export const useInvalidateUsageQueries = () => {
  const client = useQueryClient();
  return () => {
    client.invalidateQueries({ queryKey: settingsKeys.usage() });
    client.invalidateQueries({ queryKey: settingsKeys.usageTimeline() });
    // Invalidate all daily usage queries
    client.invalidateQueries({ queryKey: [...settingsKeys.root, 'daily_usage'] });
  };
};

export const useActionLogs = (limit: number) => {
  return useQuery<ActionLogEntry[], Error>(settingsKeys.logs(limit), () => fetchActionLogs(limit), {
    refetchInterval: 60_000,
  });
};

export const useApprovalRoleMappings = () => {
  return useQuery<Record<string, WorkspaceRole>, Error>(
    settingsKeys.approvalRoles(),
    fetchApprovalRoleMappings,
    {
      staleTime: 60_000,
    },
  );
};

export const useUpdateApprovalRoleMappings = () => {
  const client = useQueryClient();
  return useMutation(updateApprovalRoleMappings, {
    onSuccess: () => {
      client.invalidateQueries({ queryKey: settingsKeys.approvalRoles() });
    },
  });
};
